import type { ModuleSnapshot } from '@ctcells/contract'
import { enumLabel, moduleLabel, t, type MessageKey } from '@ctcells/i18n'
import { useContext, useLayoutEffect, useRef, useState } from 'react'

import { cx } from '../../atoms/cx.ts'
import { useDwell } from '../../figures/useDwell.ts'

import { monitoringDeviceName, RAIL_PARK_LEFT, RAIL_STOPS } from './monitoringModel.ts'
import {
  robotLineStep,
  stationDoor,
  stationLayout,
  type RobotLineLayout,
  type RobotLinePlace,
  type RobotLineTrack,
  type RobotLineTray,
} from './robotLineModel.ts'
import { handoffPointLabel } from './robotTransferModel.ts'
import { TrayViewContext } from './trayViewModel.ts'

import './robotLine.css'

/**
 * **6축 가로 라인** — **위 레일에 매달린 6축**이 원심분리기 · 어셈블러 · 염색기 **위를** 스텝 따라 오가고(eric 2026-10-08 「6축 로봇이 원심분리기,
 * 어셈블러, 염색기 위에 있어야 돼」), 아래 정거장 셋은 시뮬레이터 배치(eric 「이거 보면서 해줘」)대로 — **오른쪽 슬라이드 열**(세로 레일 띠 · 트레이가
 * 후면 ↔ 전면을 오간다 · 양 끝에 뒷문 · 앞문), **왼쪽 위 거치대**(다음에 갈 트레이 · 6축이 꺼내 간다), **이름은 아래**. C 는 원심 슬라이드, A 는 원심
 * 거치대 → 어셈블러 슬라이드, L 은 어셈블러 거치대 → 염색기 슬라이드. 자리 · 동작은 `robotLineModel.ts` 가 서버 사실로만 고른다 — 사실이 없는
 * 프레임에서는 **마지막 자리를 유지**하고, 처음부터 사실이 없으면 팔을 레일 왼쪽 끝 바깥(정거장 아님)에 둔다(2026-10-06 규칙 그대로).
 * 글자 줄은 없다 — 모양이 말한다. 🔴 **움직이지 않는다** — 서버가 말한 지점(레일 자리 · 포크 상하 · 트레이 자리)에 바로 선다(eric 2026-10-26
 * 「원심분리기 어셈블러와 같이.. 움직이지 말고.. 특정 지점으로 바로 이동하는 형태로」). 미끄러짐 · 넣고 빼기 애니메이션은 걷었다.
 */
export function RobotLine({
  snapshot,
  modules,
  stale = false,
}: {
  snapshot: ModuleSnapshot | undefined
  modules: readonly ModuleSnapshot[]
  stale?: boolean | undefined
}) {
  const trays = useContext(TrayViewContext)
  /* 걸음을 따라 들고 다니는 기억(쥠 · 놓음 · 출발지) — `RobotLineTrack` 머리말. 렌더 중 파생 상태 갱신(React 허용 패턴) */
  const [track, setTrack] = useState<RobotLineTrack>({})
  const step = robotLineStep(snapshot, modules, trays, stale, track)
  if (!sameTrack(step.track, track)) setTrack(step.track)
  const view = step.view
  /* 마지막으로 안 자리 — 실행 사이(IDLE)에는 서버가 자리를 말하지 않으므로 그대로 둔다(렌더 중 파생 상태 갱신 — React 허용 패턴) */
  const [last, setLast] = useState<RobotLinePlace | undefined>(undefined)
  if (view.target !== undefined && view.target !== last) setLast(view.target)
  const at = view.target ?? last
  const rootRef = useRef<HTMLDivElement>(null)
  const [anchors, setAnchors] = useState<Readonly<Record<string, number>>>({})
  /* 레일 기준 칸 가운데(CSS px) — 크기가 바뀔 때마다(ResizeObserver 는 붙는 순간에도 한 번 부른다) 다시 잰다 */
  useLayoutEffect(() => {
    const root = rootRef.current
    if (root === null || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      const next = measureAnchors(root)
      setAnchors((prev) => (sameAnchors(prev, next) ? prev : next))
    })
    observer.observe(root)
    return () => {
      observer.disconnect()
    }
  }, [])
  const stopLeft =
    at === undefined
      ? undefined
      : at === 'park'
        ? RAIL_PARK_LEFT
        : RAIL_STOPS.find((stop) => stop.key === at)?.left
  /*
   * 정거장 «안»의 자리 — 집을 때는 거치대(왼쪽 위 · 다음에 갈 트레이), 놓을 때는 슬라이드(오른쪽 · 뒷문)(eric 2026-10-08 「어셈블러 왼쪽 위에서
   * 염색기 L 트레이로 넣어야지」). 거치대가 없는 정거장(염색기)은 집기도 슬라이드에서. 가로 어긋남은 CSS 변수(`--robotline-*-dx`) 하나씩
   */
  const targetStation = view.stations.find((st) => st.key === view.station)
  const targetLayout =
    targetStation === undefined ? undefined : stationLayout(targetStation, view.trays)
  /* 쥐고 있으면(이송 중) 어디서든 목적지의 슬라이드 위 — 가운데에 서지 않는다(eric 2026-10-08 「가로 위치가 안 맞잖아」) */
  /* 놓을 때는 그 트레이의 자리 — A 가 원심 · L 이 어셈블러로 갈 때는 거치대(`placeSideOf` · eric 2026-10-19), 그 밖은 슬라이드 */
  const placeSide =
    view.placeTo === 'holder' && targetLayout?.holder !== undefined ? 'holder' : 'slide'
  /*
   * 집을 때는 집는 트레이가 놓인 자리(`view.gripFrom` · eric 2026-10-21 「오른쪽 슬라이더에 잇는데.. 거기서 빼야지」). 모르면 종전대로 거치대가 있으면 거치대
   */
  const gripSide =
    view.gripFrom ?? (targetLayout?.holder !== undefined ? ('holder' as const) : ('slide' as const))
  const side =
    view.action === 'grip'
      ? gripSide
      : view.action === 'release' || view.carrying
        ? placeSide
        : undefined
  /*
   * 🔴 **자리는 그려진 칸에서 잰다**(eric 2026-10-08 「트레이를 놓고 빼는 위치가 우리 그림에서 안 맞아」 · V3.0 시뮬레이터는 6축 핸드오프 지점을
   *    «그 자리 트레이의 손잡이 점»으로 둔다). 종전 고정 어긋남(`--robotline-*-dx` · 정거장 = 16.67/50/83.33%)은 칸 폭과 따로 놀아 1920 에서 거치대는
   *    25px 왼쪽 · 슬라이드는 40px 오른쪽에 섰다(실측). 그려진 거치대 · 슬라이드 · 상자의 가로 가운데를 레일 기준으로 재어 그 자리에 세운다.
   *    잴 수 없으면(첫 그림 전 · jsdom) 종전 식으로 선다
   */
  const anchor =
    at === undefined || at === 'park' ? undefined : anchors[`${at}:${side ?? 'center'}`]
  const leftNow =
    anchor !== undefined
      ? `${String(anchor)}px`
      : stopLeft === undefined || at === 'park' || side === undefined
        ? stopLeft
        : `calc(${stopLeft} + var(--robotline-${side}-dx))`
  /* 🔴 최소 머무름 — 자리가 한 프레임에 두 번 바뀌면 그 자리가 눈에 안 남는다. 자리를 1.5초는 보여 준 뒤 다음 자리로 바로 옮긴다(염색기 툴과 같은 규칙) */
  const left = useDwell(leftNow, ROBOT_DWELL_MS, (a, b) => a === b)
  const carried = view.trays.filter((tray) => tray.inTransit)
  const trayName = (tray: RobotLineTray) => t(`monitoring.tray.${tray.tray}` as MessageKey)
  /*
   * 6축 표현 — 관절 팔 대신 **단순 호이스트**(레일 캐리지 + 수직 마스트 + 포크 · eric 2026-10-08 「6축 로봇이 너무 흐물거려. 단순하게 화면 좌우 상하로
   * 트레이를 넣고 빼게」). 레일 위를 좌우로(`left`), 집기 · 놓기면 포크가 아래로 내려가(`translateY`) 정거장 뒷문으로 트레이를 넣고 뺀다 — 둘 다 바로 선다.
   * 쥔 트레이(`grip` 걸음부터 놓을 때까지 · `RobotLineTrack`)는 포크 아래 트레이 모양 + 글자
   */
  const down = view.action === 'grip' || view.action === 'release'
  const arm = (
    <Hoist carried={view.carrying ? carried[0] : undefined} down={down} holding={view.carrying} />
  )
  /*
   * 트레이 판 — 글자만이 아니라 트레이 모양(3 × 3 칸 · 오른쪽 위 모서리 따냄 · eric 2026-10-08 「트레이 모양 느낌 나게」).
   * 🔴 트레이가 없는 자리에는 트레이 모양을 그리지 않는다 — 점선 빈 칸뿐(eric 「왼쪽 위에 트레이가 없어야 하는데 보여.. 점선으로 안 보이게」).
   *    모듈이 「찼음」이라 했는데 트레이 뷰가 어느 트레이인지 모르면 글자 없는 트레이 모양
   */
  const chip = (tray: RobotLineTray | undefined) =>
    tray === undefined ? (
      <TrayGlyph />
    ) : (
      <span
        className="robotline__traywrap"
        data-tray={tray.tray}
        title={`${trayName(tray)} · ${handoffPointLabel(tray.tail)}`}
      >
        <TrayGlyph />
        <b className="robotline__tray">{tray.tray}</b>
      </span>
    )

  return (
    <div
      aria-label={monitoringDeviceName('robot')}
      className="robotline"
      data-action={view.action}
      ref={rootRef}
      data-carrying={view.carrying ? 'true' : 'false'}
      data-side={side}
      role="group"
    >
      {/* 위 레일 — 6축이 매달려 정거장 «위»를 오간다. 들고 가는 트레이는 팔 옆에 표지 */}
      <div className="robotline__rail">
        <span className="robotline__park" style={{ left: RAIL_PARK_LEFT }}>
          {t('monitoring.robotLine.park')}
        </span>
        {left === undefined ? (
          /* 자리를 알면 레일 위 그 자리에 · 모르면 레일 왼쪽 끝 바깥에 둔다(정거장 어디에도 세우지 않는다) — 팔 자체(파지 · 자세)는 늘 보인다 */
          <div className="robotline__unplaced" data-part="unplaced">
            {arm}
          </div>
        ) : (
          <div className="robotline__bot" data-robot-at={at} style={{ left }}>
            {arm}
          </div>
        )}
      </div>
      <ol className="robotline__stations">
        {view.stations.map((station) => {
          const layout = stationLayout(station, view.trays)
          const { slide, holder } = layout
          return (
            <li
              className={cx(
                'robotline__station',
                view.station === station.key && 'robotline__station--now',
              )}
              data-handoff={station.handoff}
              data-station={station.key}
              key={station.key}
            >
              <div
                className={cx(
                  'robotline__box robotline__body',
                  holder === undefined && 'robotline__body--slideonly',
                )}
                data-unknown={layout.unknown ? 'true' : undefined}
              >
                {/* 왼쪽 위 거치대 — 다음에 갈 트레이가 놓이는 자리(원심 → A · 어셈블러 → L · 6축이 꺼내 간다) */}
                <div className="robotline__side">
                  {holder === undefined ? null : (
                    <span
                      className={cx(
                        'robotline__spot',
                        holder.occupied && 'robotline__spot--occupied',
                      )}
                      data-occupied={holder.occupied ? 'true' : 'false'}
                      data-part="holder"
                      data-tray={holder.tray?.tray}
                      title={handoffPointLabel('tray-1')}
                    >
                      {holder.occupied ? chip(holder.tray) : null}
                    </span>
                  )}
                </div>
                {/*
                 * 오른쪽 슬라이드 열(시뮬레이터 배치 · eric 「약간 오른쪽 편에 레일이 있고, 오른쪽 아랫부분에 뺄 수 있는 부분」) — 뒷문(위 · 6축이 넣고
                 * 빼는 문) ↔ 앞문(아래 · 사람이 여는 문) 사이의 한 슬라이드. 트레이 뷰가 말한 자리(후면 = 위 · 전면 = 아래)에 판이 바로 선다(미끄러지지
                 * 않는다). 트레이를 모르는데 모듈이 「후면 찼음」이면 후면에 빈 판, 비었으면 후면에 점선 자리
                 */}
                <div
                  className="robotline__slide"
                  data-occupied={slide.occupiedRear || slide.tray !== undefined ? 'true' : 'false'}
                  data-part="slide"
                  data-pos={slide.pos}
                  data-published={slide.published ? 'true' : 'false'}
                >
                  <Door face="rear" state={stationDoor(station.snapshot, 'rear')} />
                  <span className="robotline__track" />
                  {/*
                   * 트레이 판은 모듈 · 트레이 뷰가 말한 자리에만 선다 — 넣고 빼는 애니메이션은 걷었다(eric 2026-10-26 「움직이지 말고.. 특정 지점으로
                   * 바로 이동」). 집기 · 놓기는 포크가 그 자리 위에 내려간 모양(`Hoist` `down`) · 쥔 트레이는 포크 아래가 말한다
                   */}
                  {layout.unknown ? null : slide.tray !== undefined || slide.occupiedRear ? (
                    <span
                      className={cx(
                        'robotline__spot robotline__spot--occupied robotline__slidechip',
                        slide.pos === 'front' && 'robotline__slidechip--front',
                      )}
                      data-pos={slide.pos ?? 'rear'}
                      data-tray={slide.tray?.tray}
                    >
                      {chip(slide.tray)}
                    </span>
                  ) : slide.published ? (
                    /* 빈 자리 점선 — 모듈이 슬라이드 자리를 «비었음»으로 발행했을 때만(미발행을 «빔»으로 접지 않는다) */
                    <span className="robotline__spot robotline__slidechip" data-pos="rear" />
                  ) : null}
                  <Door face="front" state={stationDoor(station.snapshot, 'front')} />
                </div>
              </div>
              {/* 이름은 상자 «바깥» 아래(eric 2026-10-08 「아랫쪽에」 → 「바깥쪽으로 텍스트 빼달라고」) · 그 아래 미발행/반출·반입 글자 */}
              <div className="robotline__foot">
                <div className="robotline__name">{moduleLabel(station.key)}</div>
                {/*
                 * 자리를 모르는 까닭 — 모듈 없음 · 미발행 · 발행한 자리 0개(종전 「핸드오프 자리」 표의 사유 칸 · 그 표는 걷었다 eric 2026-10-21
                 * 「운송장비 그림상에 표현되고 있으면.. 이거 지워줘」). 찼음/비었음은 그림의 트레이 판 · 점선 자리가 말한다
                 */}
                {layout.note === undefined ? null : (
                  <div className="robotline__nospots" data-note={layout.note}>
                    {t(NOTE_KEY[layout.note])}
                  </div>
                )}
                {station.handoff === undefined ? null : (
                  <div className="robotline__handoff">
                    {enumLabel('enum.moduleState', station.snapshot?.state ?? '')}
                  </div>
                )}
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

/**
 * 정거장 문 한 짝 — 슬라이드 열 양 끝: 뒷문(위 · 레일 쪽) · 앞문(아래 · 사람 쪽). 글자 없이 문틀 + 손잡이(eric 2026-10-08 「앞문과 뒷문을 만들어줘」 ·
 * 「오른쪽 아래에 앞에 열 수 있는 문」). 서버가 문 상태를 주면(`interlock.doors`) 열림은 틈이 난 문, 닫힘은 꽉 찬 문 · 모르면(`null`) 그냥 문(가르지 않는다)
 */
function Door({ face, state }: { face: 'front' | 'rear'; state: 'open' | 'closed' | null }) {
  return (
    <div
      aria-hidden="true"
      className={cx(
        'robotline__door',
        `robotline__door--${face}`,
        state === 'open' && 'robotline__door--open',
      )}
      data-door={state ?? 'unknown'}
      data-face={face}
      data-part="door"
      title={t(
        face === 'front' ? 'monitoring.robotLine.door.front' : 'monitoring.robotLine.door.rear',
      )}
    >
      <i className="robotline__handle" />
    </div>
  )
}

/** 트레이 모양의 속 — 윤곽(오른쪽 위 노치 · 안쪽 귀가 둥글게 파임 · 실물 사진 2026-10-19) + 3 × 3 칸. 44 × 28 좌표 */
function TrayGlyphInner() {
  return (
    <>
      <path d="M1 3a2 2 0 0 1 2-2h35v1a3 3 0 0 0 3 3h1a1 1 0 0 1 1 1v19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2z" />
      {[0, 1, 2].flatMap((row) =>
        [0, 1, 2].map((col) => (
          <rect
            height={5}
            key={`${String(row)}-${String(col)}`}
            rx={1}
            width={9}
            x={5 + col * 11.5}
            y={5 + row * 7}
          />
        )),
      )}
    </>
  )
}

/** 트레이 모양 — 와이어프레임 트레이의 윤곽(오른쪽 위 모서리 따냄) + 3 × 3 칸. 글자가 위에 겹친다 */
function TrayGlyph() {
  return (
    <svg aria-hidden="true" className="robotline__trayfig" viewBox="0 0 44 28">
      <TrayGlyphInner />
    </svg>
  )
}

/**
 * 6축 호이스트 — 레일 캐리지(위) · 수직 마스트 · 포크(아래). 집기 · 놓기면 마스트가 늘고 포크가 내려간다(`data-down`) — 화면 상하로 트레이를
 * 넣고 뺀다. 쥔 트레이는 포크 아래 트레이 모양(+ 글자). 이동 애니메이션 없이 그 자세로 바로 선다
 */
function Hoist({
  down,
  holding,
  carried,
}: {
  down: boolean
  holding: boolean
  carried: RobotLineTray | undefined
}) {
  const drop = down ? HOIST_DROP : 0
  return (
    <svg
      aria-hidden="true"
      className="robotline__hoist"
      data-down={down ? 'true' : 'false'}
      data-holding={holding ? 'true' : 'false'}
      viewBox="0 0 120 150"
    >
      <rect className="robotline__hoist-car" height={12} rx={3} width={60} x={30} y={0} />
      <rect
        className="robotline__hoist-mast"
        data-part="mast"
        height={52 + drop}
        style={{ height: `${String(52 + drop)}px` }}
        width={8}
        x={56}
        y={10}
      />
      <g
        className="robotline__hoist-z"
        data-down={down ? 'true' : 'false'}
        data-part="hoist-z"
        style={{ transform: `translateY(${String(drop)}px)` }}
      >
        {/*
          포크 — 🔴 **두께는 굵은 선이 아니라 «모양»(면)**이다(2026-10-11 · 장비 그림 선 굵기 통일 — 굵기 4 선을 걷고 캐리지 · 마스트와
             같은 채운 막대로). 가로 팔 하나 + 양 끝 세로 손가락 둘 — 종전 선 경로(M30 62h60 · M34/M86 62v12)와 같은 자리 · 같은 두께(4)
        */}
        <g className="robotline__hoist-fork" data-part="fork">
          <rect height={4} rx={2} width={64} x={28} y={60} />
          <rect height={16} rx={2} width={4} x={32} y={60} />
          <rect height={16} rx={2} width={4} x={84} y={60} />
        </g>
        {holding ? (
          <g data-part="held" data-tray={carried?.tray} transform="translate(38 66)">
            <g className="robotline__trayfig robotline__trayfig--held">
              <TrayGlyphInner />
            </g>
            {carried === undefined ? null : (
              <text className="robotline__hoist-letter" textAnchor="middle" x={22} y={18}>
                {carried.tray}
              </text>
            )}
          </g>
        ) : null}
      </g>
    </svg>
  )
}

const NOTE_KEY = {
  missing: 'monitoring.robot.missing',
  unpublished: 'monitoring.robot.spots.unpublished',
  none: 'monitoring.robot.spots.none',
} as const satisfies Record<NonNullable<RobotLineLayout['note']>, MessageKey>

/** 포크가 내려가는 거리(그림 좌표) — 레일 띠 아래 정거장 뒷문까지 */
const HOIST_DROP = 60

/** 레일 위 자리 하나에 최소 머무는 시간 — 서버 프레임이 빠르게 바뀌어도 눈에 남게(eric 2026-10-08 「들고 왔다갔다」) */
export const ROBOT_DWELL_MS = 1500

/** 정거장마다 상자 · 거치대 · 슬라이드의 가로 가운데 — 레일 왼쪽 기준 CSS px(칸 배율을 되돌린다). 잴 수 없으면 빈 표 */
function measureAnchors(root: HTMLElement): Record<string, number> {
  const rail = root.querySelector<HTMLElement>('.robotline__rail')
  if (rail === null || rail.offsetWidth === 0) return {}
  const rr = rail.getBoundingClientRect()
  if (rr.width === 0) return {}
  const zoom = rr.width / rail.offsetWidth
  const out: Record<string, number> = {}
  for (const station of root.querySelectorAll<HTMLElement>('[data-station]')) {
    const key = station.dataset['station']
    if (key === undefined) continue
    const parts: readonly (readonly [string, string])[] = [
      ['center', '.robotline__box'],
      ['holder', '[data-part="holder"]'],
      ['slide', '[data-part="slide"]'],
    ]
    for (const [part, selector] of parts) {
      const el = station.querySelector(selector)
      if (el === null) continue
      const r = el.getBoundingClientRect()
      if (r.width === 0) continue
      out[`${key}:${part}`] = Math.round(((r.left + r.width / 2 - rr.left) / zoom) * 10) / 10
    }
  }
  return out
}

function sameAnchors(a: Readonly<Record<string, number>>, b: Readonly<Record<string, number>>) {
  const ka = Object.keys(a)
  return ka.length === Object.keys(b).length && ka.every((k) => a[k] === b[k])
}

function sameTrack(a: RobotLineTrack, b: RobotLineTrack): boolean {
  return (
    a.gripped === b.gripped &&
    a.releasing === b.releasing &&
    a.released === b.released &&
    a.from === b.from &&
    a.fromSide === b.fromSide
  )
}
